import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IEmailTemplate, IEmailTemplateMeta, IEmailTemplateCategoryOption,
  CommunicationTemplateType,
  fetchEmailTemplates, fetchEmailTemplateMeta,
} from "../../Api/emailTemplatesApi";

const STORAGE_KEY_FAVORITES = "dcc.emailComposer.favoriteTemplates";
const STORAGE_KEY_RECENTS   = "dcc.emailComposer.recentTemplates";
const RECENT_LIMIT = 6;

/** Favorites + recents are scoped per template type so the email
 *  picker's stars/recents don't bleed into the call-log picker and
 *  vice versa. Keys are namespaced with the type when one is in
 *  scope; legacy callers (no type) keep the original keys for
 *  back-compat. */
const favoritesKey = (type?: CommunicationTemplateType): string =>
  type ? `${STORAGE_KEY_FAVORITES}.${type}` : STORAGE_KEY_FAVORITES;
const recentsKey   = (type?: CommunicationTemplateType): string =>
  type ? `${STORAGE_KEY_RECENTS}.${type}`   : STORAGE_KEY_RECENTS;

function safeRead<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch { return fallback; }
}

function safeWrite(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

export interface UseTemplateLibrary {
  templates:  IEmailTemplate[];
  categories: IEmailTemplateCategoryOption[];
  favorites:  number[];
  recents:    number[];
  loading:    boolean;
  error:      string | null;
  reload:     () => Promise<void>;

  toggleFavorite: (templateId: number) => void;
  markUsed:       (templateId: number) => void;

  /** Lookup a template by id from the loaded list. */
  byId: (id: number) => IEmailTemplate | undefined;
}

export interface UseTemplateLibraryOptions {
  /** Filter the loaded list to one template type (e.g. "call_log").
   *  Omitted → loads every active template, like the original behaviour. */
  type?: CommunicationTemplateType;
}

/**
 * Shared hook that powers every composer template surface.
 * Loads the active template list + category schema once, then layers
 * a localStorage-backed "favorites" + "recently used" memory on top
 * so the quick-chips and selector modal stay personal per browser.
 *
 * Called by:
 *   - EmailComposerToolbar             (Email Hub Reply + Lead Send)
 *   - CommunicationTemplateToolbar     (Quick Actions: call/note/meeting)
 *
 * Categories returned by the hook automatically narrow to the requested
 * type so callers can render the right cascading dropdown without
 * needing to know about per-type maps.
 */
export function useTemplateLibrary(options: UseTemplateLibraryOptions = {}): UseTemplateLibrary {
  const { type } = options;

  const [templates, setTemplates]   = useState<IEmailTemplate[]>([]);
  const [meta, setMeta]             = useState<IEmailTemplateMeta | null>(null);
  const [favorites, setFavorites]   = useState<number[]>(() => safeRead<number[]>(favoritesKey(type), []));
  const [recents, setRecents]       = useState<number[]>(()   => safeRead<number[]>(recentsKey(type),   []));
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Active templates only — staff shouldn't see inactive
      // drafts in the send picker. Filter by type when one was
      // requested so a "call_log" picker doesn't return emails.
      const [list, m] = await Promise.all([
        fetchEmailTemplates({ status: "active", template_type: type }),
        meta ? Promise.resolve(meta) : fetchEmailTemplateMeta(),
      ]);
      setTemplates(list);
      if (!meta && m && "categories" in m) setMeta(m as IEmailTemplateMeta);
    } catch (e: any) {
      setError(e?.message || "Failed to load templates.");
    }
    setLoading(false);
  }, [meta, type]);

  useEffect(() => { reload(); }, [reload]);

  // When the caller switches type at runtime, re-seed favorites/
  // recents from the type-scoped storage keys so the picker UI
  // shows the right starred items.
  useEffect(() => {
    setFavorites(safeRead<number[]>(favoritesKey(type), []));
    setRecents(safeRead<number[]>(recentsKey(type), []));
  }, [type]);

  const toggleFavorite = useCallback((templateId: number) => {
    setFavorites(prev => {
      const next = prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [templateId, ...prev];
      safeWrite(favoritesKey(type), next);
      return next;
    });
  }, [type]);

  const markUsed = useCallback((templateId: number) => {
    setRecents(prev => {
      const filtered = prev.filter(id => id !== templateId);
      const next = [templateId, ...filtered].slice(0, RECENT_LIMIT);
      safeWrite(recentsKey(type), next);
      return next;
    });
  }, [type]);

  const byId = useCallback(
    (id: number) => templates.find(t => t.id === id),
    [templates],
  );

  // Narrow the surfaced categories to the requested type's map when
  // one is known. Falls back to the flat list (back-compat) otherwise.
  const categories = useMemo(() => {
    if (type && meta?.categories_by_type?.[type]) {
      return meta.categories_by_type[type];
    }
    return meta?.categories ?? [];
  }, [meta, type]);

  return { templates, categories, favorites, recents, loading, error, reload, toggleFavorite, markUsed, byId };
}
